import { useState, useEffect, useRef, useCallback } from 'react'
import { Spinner } from 'react-bootstrap'
import { destinationsApi, recordingsApi, ivrApi } from '../../services/api'
import '../../styles/ivr-flowchart.css'

interface IVROption {
  id?: string
  digit: string
  action_type: string
  action_target: string
}

interface IVRMenu {
  id: string
  name: string
  description: string
  greeting_recording_id: string
  greeting_type?: string
  timeout: number
  timeout_retries: number
  invalid_retries: number
  timeout_action_type: string
  timeout_action_target: string
  invalid_action_type: string
  invalid_action_target: string
  is_active: boolean
  options: IVROption[]
}

interface ResolvedDest {
  icon: string
  name: string
  typeName: string
  type: string
}

const TYPE_ICONS: Record<string, string> = {
  EXTENSION: 'telephone',
  RING_GROUP: 'people',
  QUEUE: 'collection',
  IVR: 'diagram-3',
  TIME_CONDITION: 'clock',
  CONFERENCE: 'camera-video',
  VOICEMAIL: 'voicemail',
  FAX: 'printer',
  RECORDING: 'file-music',
  PARKING: 'p-square',
  NIGHT_MODE: 'moon',
  ANNOUNCEMENT: 'megaphone',
  EXTERNAL_DEST: 'telephone-outbound-fill',
  EXTERNAL: 'telephone-outbound',
  HANGUP: 'telephone-x',
  CALLBACK: 'reply',
}

const TYPE_NAMES: Record<string, string> = {
  EXTENSION: 'Nebenstelle',
  RING_GROUP: 'Rufgruppe',
  QUEUE: 'Warteschlange',
  IVR: 'IVR-Menü',
  TIME_CONDITION: 'Zeitbedingung',
  CONFERENCE: 'Konferenz',
  VOICEMAIL: 'Voicemail',
  FAX: 'Fax',
  RECORDING: 'Ansage',
  PARKING: 'Parken',
  NIGHT_MODE: 'Nachtmodus',
  ANNOUNCEMENT: 'Ansage',
  EXTERNAL_DEST: 'Externes Ziel',
  EXTERNAL: 'Externe Nr.',
  HANGUP: 'Auflegen',
  CALLBACK: 'Rückruf',
}

function destKey(type: string, target: string) {
  return `${type}::${target}`
}

// Max depth for recursive IVR rendering to prevent infinite loops
const MAX_DEPTH = 5

interface IVRFlowchartProps {
  menu: IVRMenu
  depth?: number
  visitedIds?: Set<string>
}

export default function IVRFlowchart({ menu, depth = 0, visitedIds }: IVRFlowchartProps) {
  const [resolved, setResolved] = useState<Map<string, ResolvedDest>>(new Map())
  const [loading, setLoading] = useState(true)
  const [greetingName, setGreetingName] = useState<string | null>(null)
  const [subIVRs, setSubIVRs] = useState<Map<string, IVRMenu>>(new Map())
  const branchesRef = useRef<HTMLDivElement>(null)

  // Track visited IVR IDs to prevent circular references
  const visited = visitedIds || new Set<string>()
  visited.add(menu.id)

  // Collect all destinations that need resolving
  const collectDestinations = useCallback(() => {
    const dests: { type: string; target: string }[] = []
    for (const opt of menu.options || []) {
      if (opt.action_type && opt.action_type !== 'hangup') {
        dests.push({ type: opt.action_type, target: opt.action_target })
      }
    }
    if (menu.timeout_action_type && menu.timeout_action_type !== 'hangup') {
      dests.push({ type: menu.timeout_action_type, target: menu.timeout_action_target })
    }
    if (menu.invalid_action_type && menu.invalid_action_type !== 'hangup') {
      dests.push({ type: menu.invalid_action_type, target: menu.invalid_action_target })
    }
    return dests
  }, [menu])

  useEffect(() => {
    let cancelled = false

    async function resolve() {
      setLoading(true)
      const map = new Map<string, ResolvedDest>()
      const dests = collectDestinations()

      // Resolve all destinations in parallel
      const results = await Promise.allSettled(
        dests.map(async (d) => {
          try {
            const res = await destinationsApi.resolve(d.type, d.target)
            const data = res.data
            return {
              key: destKey(d.type, d.target),
              resolved: {
                icon: data.type_icon || TYPE_ICONS[d.type.toUpperCase()] || 'arrow-right',
                name: data.name || data.display || d.target,
                typeName: data.type_name || TYPE_NAMES[d.type.toUpperCase()] || d.type,
                type: d.type,
              } as ResolvedDest,
            }
          } catch {
            return {
              key: destKey(d.type, d.target),
              resolved: {
                icon: TYPE_ICONS[d.type.toUpperCase()] || 'arrow-right',
                name: d.target || '—',
                typeName: TYPE_NAMES[d.type.toUpperCase()] || d.type,
                type: d.type,
              } as ResolvedDest,
            }
          }
        })
      )

      for (const r of results) {
        if (r.status === 'fulfilled') {
          map.set(r.value.key, r.value.resolved)
        }
      }

      // Resolve greeting recording name
      if (menu.greeting_recording_id) {
        try {
          const res = await recordingsApi.list()
          const rec = (res.data.items || []).find(
            (r: { id: string; name: string }) => r.id === menu.greeting_recording_id
          )
          if (rec && !cancelled) setGreetingName(rec.name)
        } catch {
          // ignore
        }
      }

      // Load sub-IVRs for chained menus
      if (depth < MAX_DEPTH) {
        const ivrDests = dests.filter(d => d.type.toUpperCase() === 'IVR' && d.target && !visited.has(d.target))
        const uniqueIds = [...new Set(ivrDests.map(d => d.target))]
        const subMap = new Map<string, IVRMenu>()

        await Promise.allSettled(
          uniqueIds.map(async (id) => {
            try {
              const res = await ivrApi.get(id)
              if (res.data && !cancelled) subMap.set(id, res.data)
            } catch {
              // Sub-IVR could not be loaded
            }
          })
        )

        if (!cancelled) setSubIVRs(subMap)
      }

      if (!cancelled) {
        setResolved(map)
        setLoading(false)
      }
    }

    resolve()
    return () => { cancelled = true }
  }, [menu, collectDestinations, depth])

  // Update horizontal rail width via CSS custom property after layout
  useEffect(() => {
    const el = branchesRef.current
    if (!el || loading) return

    const measure = () => {
      const children = el.querySelectorAll(':scope > .ivr-flow-branch')
      if (children.length < 2) {
        el.style.setProperty('--rail-half-width', '0px')
        return
      }
      const first = children[0] as HTMLElement
      const last = children[children.length - 1] as HTMLElement
      const containerRect = el.getBoundingClientRect()
      const firstCenter = first.getBoundingClientRect().left + first.getBoundingClientRect().width / 2 - containerRect.left
      const lastCenter = last.getBoundingClientRect().left + last.getBoundingClientRect().width / 2 - containerRect.left
      const halfSpan = (lastCenter - firstCenter) / 2
      el.style.setProperty('--rail-half-width', `${halfSpan}px`)
    }

    requestAnimationFrame(measure)
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [loading])

  function getGreetingLabel(): string {
    if (menu.greeting_type === 'tts') return 'TTS-Ansage'
    if (greetingName) return greetingName
    if (menu.greeting_recording_id) return 'Ansage'
    return 'Keine Ansage'
  }

  function renderDest(type: string, target: string) {
    if (!type) {
      return (
        <div className="ivr-flow-dest ivr-flow-dest--empty">
          <div className="ivr-flow-dest-icon"><i className="bi bi-dash"></i></div>
          <div className="ivr-flow-dest-name">Nicht konfiguriert</div>
        </div>
      )
    }

    const upperType = type.toUpperCase()
    if (upperType === 'HANGUP') {
      return (
        <div className="ivr-flow-dest ivr-flow-dest--hangup">
          <div className="ivr-flow-dest-icon"><i className="bi bi-telephone-x"></i></div>
          <div className="ivr-flow-dest-name">Auflegen</div>
          <div className="ivr-flow-dest-type">Hangup</div>
        </div>
      )
    }

    // Check if this is a sub-IVR that we can render inline
    if (upperType === 'IVR' && target && subIVRs.has(target)) {
      const subMenu = subIVRs.get(target)!
      return (
        <div className="ivr-flow-sub-ivr">
          <IVRFlowchart menu={subMenu} depth={depth + 1} visitedIds={new Set(visited)} />
        </div>
      )
    }

    const r = resolved.get(destKey(type, target))
    if (!r) {
      return (
        <div className="ivr-flow-dest">
          <div className="ivr-flow-dest-icon">
            <i className={`bi bi-${TYPE_ICONS[upperType] || 'arrow-right'}`}></i>
          </div>
          <div className="ivr-flow-dest-name">{target || '—'}</div>
          <div className="ivr-flow-dest-type">{TYPE_NAMES[upperType] || type}</div>
        </div>
      )
    }

    // For IVR targets we couldn't load (circular or too deep), show with indicator
    if (upperType === 'IVR' && target) {
      const isCircular = visited.has(target)
      return (
        <div className="ivr-flow-dest ivr-flow-dest--ivr-ref">
          <div className="ivr-flow-dest-icon"><i className="bi bi-diagram-3"></i></div>
          <div className="ivr-flow-dest-name">{r.name}</div>
          <div className="ivr-flow-dest-type">
            {isCircular ? 'IVR (Zirkelverweis)' : depth >= MAX_DEPTH ? 'IVR (max. Tiefe)' : r.typeName}
          </div>
        </div>
      )
    }

    return (
      <div className="ivr-flow-dest">
        <div className="ivr-flow-dest-icon"><i className={`bi bi-${r.icon}`}></i></div>
        <div className="ivr-flow-dest-name">{r.name}</div>
        <div className="ivr-flow-dest-type">{r.typeName}</div>
      </div>
    )
  }

  // Build branches: digit options + timeout + invalid
  const branches: { label: string; type: string; target: string; variant?: string }[] = []

  const sortedOptions = [...(menu.options || [])].sort((a, b) => {
    const order = '1234567890*#'
    return order.indexOf(a.digit) - order.indexOf(b.digit)
  })

  for (const opt of sortedOptions) {
    branches.push({ label: opt.digit, type: opt.action_type, target: opt.action_target })
  }

  if (menu.timeout_action_type) {
    branches.push({ label: 'T', type: menu.timeout_action_type, target: menu.timeout_action_target, variant: 'timeout' })
  }
  if (menu.invalid_action_type) {
    branches.push({ label: '!', type: menu.invalid_action_type, target: menu.invalid_action_target, variant: 'invalid' })
  }

  if (loading) {
    return (
      <div className="ivr-flow">
        <Spinner animation="border" size="sm" className="me-2" />
        <span className="text-muted small">Flowchart wird geladen...</span>
      </div>
    )
  }

  return (
    <div className={`ivr-flow${depth > 0 ? ' ivr-flow--nested' : ''}`}>
      {/* Root node */}
      <div className="ivr-flow-root">
        <div className="ivr-flow-root-name">
          <i className="bi bi-diagram-3 me-1"></i>
          {menu.name}
        </div>
        <div className="ivr-flow-root-sub">{getGreetingLabel()}</div>
      </div>

      {branches.length > 0 && (
        <>
          <div className="ivr-flow-line" />

          <div
            className="ivr-flow-branches"
            ref={branchesRef}
            style={{
              '--rail-half-width': '0px',
              '--rail-offset': '0px',
            } as React.CSSProperties}
          >
            {branches.map((b, i) => (
              <div
                key={i}
                className={`ivr-flow-branch${b.variant ? ` ivr-flow-branch--${b.variant}` : ''}`}
              >
                <div className="ivr-flow-digit">{b.label}</div>
                <div className="ivr-flow-branch-line" />
                {renderDest(b.type, b.target)}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
